import React, { useEffect, useState } from "react";
import { AlertTriangle, PackageCheck, Layers, Scissors, Boxes, TrendingDown, ArrowDownToLine, ArrowUpFromLine, Recycle, Filter, Clock, Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtIDR, formatApiError, wibDateISO } from "../../lib/api";
import { PageHeader, Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 1 });
const IND = { red: ["Terlambat", "text-status-rejected", "bg-status-rejected"], amber: ["Segera", "text-amber-600", "bg-amber-500"], purple: ["Rework", "text-purple-600", "bg-purple-500"], green: ["On track", "text-status-passed", "bg-status-passed"] };
const today = () => wibDateISO();
const monthStart = () => today().slice(0, 8) + "01";

function WipeDialog({ open, setOpen, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setConfirm(""); setCounts(null);
      api.post("/production/admin/wipe-production", { confirm: "KOSONGKAN", dry_run: true })
        .then((r) => setCounts(r.data.counts)).catch(() => {});
    }
  }, [open]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/production/admin/wipe-production", { confirm: confirm.trim() });
      toast.success(r.data.message || "Data produksi dikosongkan");
      setOpen(false); onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const LABELS = {
    fabrics: "Master Kain", accessories: "Master Aksesori", boms: "Resep (BOM)",
    production_plans: "Rencana Produksi", cutting_orders: "Order Cutting",
    production_orders: "Order Jahit/Konveksi", material_pos: "PO Material",
    material_prs: "PR Material", material_adjustments: "Penyesuaian Material",
    material_stock: "Stok Material", material_movements: "Pergerakan Material",
    finished_goods_movements: "Pergerakan Barang Jadi (produksi)",
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="wipe-dialog">
        <DialogHeader>
          <DialogTitle className="text-status-rejected flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Kosongkan Semua Data Produksi</DialogTitle>
          <DialogDescription>
            Tindakan ini menghapus SELURUH data modul Produksi (Master Kain & Aksesori, BOM, Rencana,
            Cutting, Order Jahit, Purchase Material, stok material, dan stok barang jadi hasil produksi).
            Stok & penjualan POS di luar produksi TIDAK terpengaruh. Tindakan tidak dapat dibatalkan.
          </DialogDescription>
        </DialogHeader>
        {counts && (
          <div className="bg-ivory/60 rounded-lg p-3 text-sm max-h-56 overflow-auto" data-testid="wipe-counts">
            <div className="font-medium text-cocoa mb-1">Data yang akan dihapus:</div>
            {Object.entries(counts).filter(([k]) => LABELS[k]).map(([k, v]) => (
              <div key={k} className="flex justify-between py-0.5 border-b border-taupe/10 last:border-0">
                <span className="text-wmuted">{LABELS[k]}</span><b className="text-cocoa">{v}</b>
              </div>
            ))}
          </div>
        )}
        <div>
          <div className="text-[11px] text-wmuted mb-1">Ketik <b>KOSONGKAN</b> untuk konfirmasi</div>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="KOSONGKAN" data-testid="wipe-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="wipe-cancel-btn">Batal</Button>
          <Button disabled={confirm.trim().toUpperCase() !== "KOSONGKAN" || busy} onClick={run}
            className="bg-status-rejected text-white hover:bg-status-rejected/90" data-testid="wipe-confirm-btn">
            {busy ? "Menghapus..." : "Ya, Kosongkan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const Card = ({ icon: Icon, label, value, unit, sub, tone = "cocoa", testid }) => (
  <div className="bg-white border border-taupe/20 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-wmuted text-[11px]"><Icon className="w-4 h-4" />{label}</div>
    <div className={`text-2xl font-heading font-bold mt-1 text-${tone}`}>{value}<span className="text-xs font-normal text-wmuted ml-1">{unit}</span></div>
    {sub && <div className="text-[11px] font-semibold text-wmuted mt-0.5">{sub}</div>}
  </div>
);

export default function ProductionDashboard() {
  const { user } = useAuth();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [d, setD] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const load = () => api.get("/production/dashboard-overview", { params: { start, end } }).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!d) return <div className="p-8 text-center text-wmuted">Memuat dashboard...</div>;
  const c = d.cards;
  const pb = d.produksi_bulan;
  const ach = Math.min(100, pb.achievement);
  const maxTop = Math.max(1, ...d.top_konveksi.map((t) => t.pcs));

  return (
    <div data-testid="prod-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <PageHeader title="Dashboard WIP & QC" subtitle="Rekap produksi untuk keputusan strategis" />
        {user?.role === "owner" && (
          <Button variant="outline" onClick={() => setWipeOpen(true)} className="border-status-rejected/40 text-status-rejected hover:bg-status-rejected/10" data-testid="wipe-production-btn">
            <Trash2 className="w-4 h-4 mr-1.5" />Kosongkan Data Produksi
          </Button>
        )}
      </div>
      {user?.role === "owner" && <WipeDialog open={wipeOpen} setOpen={setWipeOpen} onDone={load} />}


      <div className="flex flex-wrap items-end gap-2 mb-4 bg-white border border-taupe/20 rounded-lg p-3">
        <div><div className="text-[11px] text-wmuted mb-1">Dari tanggal</div><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="dash-start" /></div>
        <div><div className="text-[11px] text-wmuted mb-1">s/d</div><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="dash-end" /></div>
        <Button onClick={load} className="bg-cocoa text-ivory" data-testid="dash-filter-btn"><Filter className="w-4 h-4 mr-1.5" />Filter</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-3">
        <Card icon={ArrowDownToLine} label="Kain Masuk / PO (Roll)" value={num(c.kain_masuk)} sub={fmtIDR(c.kain_masuk_value)} tone="status-passed" testid="c-kain-masuk" />
        <Card icon={ArrowUpFromLine} label="Kain Keluar (Yard)" value={num(c.kain_keluar)} tone="cocoa" testid="c-kain-keluar" />
        <Card icon={Scissors} label="Cutting Berjalan (Pcs)" value={num(c.cutting_berjalan)} tone="cocoa" testid="c-cutting" />
        <Card icon={Layers} label="Outstanding WIP (Pcs)" value={num(c.outstanding)} tone="amber-600" testid="c-wip" />
        <Card icon={PackageCheck} label="Barang Jadi (Pcs)" value={num(c.barang_jadi)} tone="status-passed" testid="c-jadi" />
        <Card icon={TrendingDown} label="Defect Rate (%)" value={num(c.defect_rate)} unit="%" tone={c.defect_rate > 5 ? "status-rejected" : "status-passed"} testid="c-defect" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <Card icon={Boxes} label="Aksesoris Masuk (Pcs)" value={num(c.aks_masuk)} sub={fmtIDR(c.aks_masuk_value)} tone="status-passed" testid="c-aks-masuk" />
        <Card icon={ArrowUpFromLine} label="Aksesoris Keluar (Pcs)" value={num(c.aks_keluar)} tone="cocoa" testid="c-aks-keluar" />
        <Card icon={Recycle} label="Kain Sisa / Remnant (Yard)" value={num(c.kain_sisa)} tone="cocoa" testid="c-sisa" />
      </div>

      {d.assets && (
        <div className="mb-5">
          <div className="text-sm font-heading font-bold text-cocoa mb-2">Total Aset Produksi (Nilai Stok Saat Ini)</div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card icon={Layers} label={`Stok Kain · ${num(d.assets.stok_kain_qty)} yd`} value={fmtIDR(d.assets.stok_kain_value)} tone="status-passed" testid="a-kain" />
            <Card icon={Scissors} label={`Kain di Cutting · ${num(d.assets.cutting_qty)} yd`} value={fmtIDR(d.assets.cutting_value)} tone="amber-600" testid="a-cutting" />
            <Card icon={Building2} label={`Kain di Konveksi · ${num(d.assets.konveksi_qty)} yd`} value={fmtIDR(d.assets.konveksi_value)} tone="cocoa" testid="a-konveksi" />
            <Card icon={Boxes} label={`Aksesoris · ${num(d.assets.aksesoris_qty)} pcs`} value={fmtIDR(d.assets.aksesoris_value)} tone="status-passed" testid="a-aks" />
            <Card icon={PackageCheck} label="TOTAL ASET PRODUKSI" value={fmtIDR(d.assets.total_asset)} tone="cocoa" testid="a-total" />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <Panel className="p-4 lg:col-span-2">
          <div className="text-sm font-heading font-bold text-cocoa mb-2 flex items-center gap-2"><Clock className="w-4 h-4" />Deadline Produksi (Outstanding)</div>
          {d.deadline.length ? d.deadline.map((x, i) => { const [lbl, cls] = IND[x.indicator] || IND.green; return (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-taupe/10 last:border-0" data-testid={`dash-deadline-${i}`}>
              <div><div className="text-sm text-cocoa font-medium">{x.product}</div><div className="text-[11px] text-wmuted">{x.partner} · target {x.target_date ? String(x.target_date).slice(0, 10) : "-"}</div></div>
              <span className={`text-xs font-medium ${cls} flex items-center gap-1`}>{x.indicator === "red" && <AlertTriangle className="w-3.5 h-3.5" />}{lbl}{x.days_late > 0 ? ` ${x.days_late} hr` : ""}</span>
            </div>
          ); }) : <div className="text-sm text-status-passed py-6 text-center">Tidak ada outstanding — semua order sudah diterima.</div>}
        </Panel>

        <Panel className="p-4">
          <div className="text-sm font-heading font-bold text-cocoa mb-3">Produksi Bulan Ini</div>
          <div className="flex items-center gap-4">
            <div className="relative w-28 h-28 rounded-full" style={{ background: `conic-gradient(#6b8e5a ${ach * 3.6}deg, #efe9e1 0deg)` }} data-testid="dash-achievement">
              <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center"><div className="text-xl font-heading font-bold text-cocoa">{pb.achievement}%</div><div className="text-[10px] text-wmuted">achievement</div></div>
            </div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between gap-4"><span className="text-wmuted">Target</span><b className="text-cocoa">{num(pb.target)} pcs</b></div>
              <div className="flex justify-between gap-4"><span className="text-wmuted">Realisasi</span><b className="text-status-passed">{num(pb.realisasi)} pcs</b></div>
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel className="p-4">
          <div className="text-sm font-heading font-bold text-cocoa mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" />Top Konveksi (Barang Jadi, periode)</div>
          {d.top_konveksi.length ? d.top_konveksi.map((t, i) => (
            <div key={i} className="mb-2" data-testid={`dash-top-${i}`}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-cocoa font-medium">{t.partner}</span><span className="text-wmuted">{num(t.pcs)} pcs</span></div>
              <div className="h-2.5 bg-ivory rounded-full overflow-hidden"><div className="h-full bg-cocoa" style={{ width: `${(t.pcs / maxTop) * 100}%` }} /></div>
            </div>
          )) : <div className="text-sm text-wmuted py-6 text-center">Belum ada barang jadi pada periode ini.</div>}
        </Panel>

        <Panel className="p-4">
          <div className="text-sm font-heading font-bold text-cocoa mb-3">Sinkronisasi Cutting vs Barang Datang</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-ivory/60 rounded-lg p-3"><div className="text-[11px] text-wmuted">Cutting Keluar</div><div className="text-xl font-heading font-bold text-cocoa" data-testid="sync-cut">{num(d.sinkron.cutting_keluar)}</div></div>
            <div className="bg-ivory/60 rounded-lg p-3"><div className="text-[11px] text-wmuted">Barang Datang</div><div className="text-xl font-heading font-bold text-status-passed" data-testid="sync-recv">{num(d.sinkron.barang_datang)}</div></div>
            <div className="bg-ivory/60 rounded-lg p-3"><div className="text-[11px] text-wmuted">Selisih</div><div className={`text-xl font-heading font-bold ${d.sinkron.selisih > 0 ? "text-status-rejected" : "text-status-passed"}`} data-testid="sync-diff">{num(d.sinkron.selisih)}</div></div>
          </div>
          <p className="text-[11px] text-wmuted mt-2">Selisih = pcs keluar cutting yang belum kembali sebagai barang jadi (masih di konveksi / dalam proses) pada periode terpilih.</p>
        </Panel>
      </div>
    </div>
  );
}
